import * as React from "react";
import Container from "@mui/material/Container";

const faqData = [
  {
    id: "panel1",
    question: "How can I contact you with questions or issues?",
    answer: "You can reach our customer support team by emailing support@koinapp.co. We're here to assist you promptly.",
  },
  {
    id: "panel2",
    question: "How do I participate in campaigns?",
    answer: "After logging in and installing the app, you'll be able to explore available campaigns from brands. Follow the campaign instructions to complete defined actions and earn incentives.",
  },
  {
    id: "panel3",
    question: "How long does it take to receive incentives?",
    answer: "Campaign submissions are reviewed and validated by our team. Incentives are processed and paid out within 24-72 hours after successful verification. Payment timelines may vary based on submission volume.",
  },
  {
    id: "panel4",
    question: "How do I receive my incentive payout?",
    answer: "You can receive payouts through UPI. Enter your UPI ID after completing a campaign, and once your submission is verified and approved, the incentive will be transferred to your account.",
  },
  {
    id: "panel5",
    question: "Are incentives guaranteed?",
    answer: "Incentives are not guaranteed and depend on successful completion and validation of campaign requirements. All submissions must meet campaign criteria to be eligible for incentive payout. Fraudulent or invalid activity may result in disqualification.",
  }
];

export default function FAQ() {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange = (panel: string) => {
    setExpanded((prev) => (prev === panel ? false : panel));
  };

  return (
    <Container id="faq" maxWidth="lg" sx={{ pt: { xs: 9, md: 13 }, pb: { xs: 6, md: 9 }, scrollMarginTop: "80px" }}>
      <div className="grid gap-10 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-5">
          <h2 className="text-[2rem] md:text-[2.75rem] font-bold text-gray-800 leading-tight tracking-tight" style={{ fontFamily: "General-Sans-bold, sans-serif" }}>
            Common questions
          </h2>
          <p className="mt-4 text-[1.05rem] text-gray-600 leading-relaxed max-w-sm">
            Can't find your answer? Write to us at{" "}
            <a href="mailto:support@koinapp.co" className="font-semibold text-orange-700 underline underline-offset-4 decoration-orange-300 hover:decoration-orange-600">
              support@koinapp.co
            </a>
          </p>
          <a
            href="/help"
            className="mt-6 inline-flex items-center gap-2 rounded-full border-[1.5px] border-gray-200 bg-white px-5 py-2.5 text-[0.95rem] font-semibold text-gray-800 hover:border-amber-400 hover:bg-[#FFF8EC] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
          >
            Visit help center
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        <ul className="md:col-span-7 border-t border-gray-200">
          {faqData.map(({ id, question, answer }) => {
            const isOpen = expanded === id;
            return (
              <li key={id} className="border-b border-gray-200">
                <h3>
                  <button
                    id={`${id}-q`}
                    aria-expanded={isOpen}
                    aria-controls={`${id}-a`}
                    onClick={() => handleChange(id)}
                    className="group flex w-full items-center justify-between gap-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded-lg"
                  >
                    <span className={`text-[1.07rem] font-semibold transition-colors ${isOpen ? "text-orange-700" : "text-gray-800 group-hover:text-orange-700"}`}>
                      {question}
                    </span>
                    <span
                      aria-hidden="true"
                      className={`grid h-9 w-9 flex-shrink-0 place-items-center rounded-full transition-all duration-300 ${isOpen ? "rotate-45 bg-gradient-to-br from-amber-400 to-orange-500 text-white" : "bg-[#FFF3DC] text-orange-700"}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                  </button>
                </h3>
                <div
                  id={`${id}-a`}
                  role="region"
                  aria-labelledby={`${id}-q`}
                  className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <p className="pb-6 pr-12 text-[0.98rem] leading-relaxed text-gray-600">{answer}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Container>
  );
}
